// ============================================================
// Prepared answer for the `replan` AI task (#68): when a truck will
// not come on time, move its loading to the first hour its gate has
// room for, pull an earlier truck into the freed slot if one is
// waiting, and contact the carrier. Plain JS: the Worker imports it.
// Demo text is Czech or English; the AI writes in any language.
// ============================================================

const toMin = (hhmm) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };
const pad = (n) => String(n).padStart(2, '0');
const toHHMM = (min) => `${pad(Math.floor(min / 60) % 24)}:${pad(min % 60)}`;
const ACTIVE = ['confirmed', 'en-route', 'no-info', 'rescheduled'];

// Loadings per gate and hour, without the problem loading itself.
function load(day, gate, skipId) {
  const perHour = {};
  day.filter(l => l.gate === gate && l.id !== skipId && ACTIVE.includes(l.state))
    .forEach(l => { const h = Math.floor(toMin(l.plannedAt) / 60); perHour[h] = (perHour[h] || 0) + 1; });
  return perHour;
}

export function replanDemo({ problem, day, capacity, now, lang }) {
  const cs = lang === 'cs';
  const cap = capacity[problem.gate] || 1;
  const perHour = load(day, problem.gate, problem.id);
  // Earliest start: the time the carrier gave, else two hours from now; loading hours 6–20.
  let start = Math.max(toMin(problem.eta || toHHMM(toMin(now) + 120)), toMin(now) + 30);
  while ((perHour[Math.floor(start / 60)] || 0) >= cap && start < 20 * 60) start = (Math.floor(start / 60) + 1) * 60;
  const tomorrow = problem.state === 'not-coming' || start >= 20 * 60;
  const newTime = tomorrow ? '06:00' : toHHMM(Math.ceil(start / 15) * 15);

  // A truck already on its way for a later slot at the same gate can take the freed one.
  const freed = toMin(problem.plannedAt);
  const pull = day.find(l => l.gate === problem.gate && l.id !== problem.id && l.state === 'en-route'
    && toMin(l.plannedAt) > freed && l.eta && toMin(l.eta) <= freed + 30);

  const steps = [
    { action: 'move', loadingId: problem.id, detail: cs
      ? `Přesunout ${problem.plate} na ${tomorrow ? 'zítra 06:00' : newTime} (brána ${problem.gate} má volno).`
      : `Move ${problem.plate} to ${tomorrow ? 'tomorrow 06:00' : newTime} (gate ${problem.gate} has room).` },
    pull && { action: 'swap', loadingId: pull.id, detail: cs
      ? `Uvolněný čas ${problem.plannedAt} nabídnout ${pull.plate} (${pull.carrier}), už je na cestě s ETA ${pull.eta}.`
      : `Offer the freed ${problem.plannedAt} slot to ${pull.plate} (${pull.carrier}), already on the way with ETA ${pull.eta}.` },
    { action: 'contact', loadingId: problem.id, detail: cs
      ? `Potvrdit nový čas s dispečinkem ${problem.carrier.replace(/\.$/, '')}.`
      : `Confirm the new time with ${problem.carrier} dispatch.` },
  ].filter(Boolean);

  return {
    summary: cs
      ? `${problem.plate} nestihne ${problem.plannedAt}. Navrhuji ${tomorrow ? 'nakládku zítra v 06:00' : `nakládku v ${newTime}`}${pull ? ` a do uvolněného času posunout ${pull.plate}` : ''}.`
      : `${problem.plate} will miss ${problem.plannedAt}. I suggest ${tomorrow ? 'loading tomorrow at 06:00' : `loading at ${newTime}`}${pull ? ` and moving ${pull.plate} into the freed slot` : ''}.`,
    newTime, dayOffset: tomorrow ? 1 : 0, steps,
    risk: cs ? `Brána ${problem.gate} zvládne ${cap} auta za hodinu.` : `Gate ${problem.gate} handles ${cap} trucks an hour.`,
  };
}
